// Copyright (c) Microsoft. All rights reserved.

import { Caption1, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import debug from 'debug';
import React from 'react';
import { Constants } from '../../Constants';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { AuthorRoles, IChatMessage } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import {
    addMessageToConversationFromUser,
    setIsLoadingMessages,
} from '../../redux/features/conversations/conversationsSlice';
import { Breakpoints, SharedStyles } from '../../styles';
import { ChatGuidance } from './ChatGuidance';
import { ChatInput } from './ChatInput';
import { ChatHistory } from './chat-history/ChatHistory';
import { EffectivePromptsDialog } from './prompt-dialog/EffectivePromptsDialog';
import { ChatScope } from '../../redux/features/conversations/ChatState';
import { IArchiveSourceAvailability } from '../../libs/models/ArchiveSourceAvailability';
import { IArchiveSources } from '../../libs/models/ArchiveSources';
import { setArchiveSourceAvailability, updateAppErrorState } from '../../redux/features/app/appSlice';
import { IAppConfigResult, useAppConfig } from '../../libs/hooks/useAppConfig';

const log = debug(Constants.debug.root).extend('chat-room');

const useClasses = makeStyles({
    root: {
        ...shorthands.overflow('hidden'),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
    },
    scroll: {
        ...shorthands.margin(tokens.spacingVerticalXS),
        ...SharedStyles.scroll,
    },
    history: {
        ...shorthands.padding(tokens.spacingVerticalM),
        marginLeft: '40px',
        ...Breakpoints.medium({
            marginLeft: '0',
        }),
        paddingRight: '40px',
        ...Breakpoints.xSmall({
            paddingRight: '5px',
        }),
        display: 'flex',
        justifyContent: 'center',
    },
    guidance: {
        display: 'block',
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    input: {
        ...shorthands.padding(tokens.spacingVerticalM),
        marginTop: '0',
        backgroundColor: '#f0f0f0',
        boxShadow: 'rgba(0, 0, 0, 0.25) 10px 0.2rem 0.4rem .075rem',
        ...shorthands.borderTop('1px solid rgba(0, 0, 0, 0.1)'),
    },
    informationTextContainer: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '100%',
        ...Breakpoints.medium({
            flexBasis: '70%',
        }),
        textAlign: 'center',
    },
    bottomContent: {
        display: 'flex',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalM,
        paddingRight: tokens.spacingHorizontalM,
        paddingLeft: tokens.spacingHorizontalM,
        paddingBottom: '15px',
        '& button': {
            display: 'flex',
            flexGrow: 1,
            flexShrink: 0,
            flexBasis: 0,
            justifyContent: 'center',
        },
    },
});

interface ChatRoomProps {
    hiddenChat: boolean;
}

export const ChatRoom: React.FC<ChatRoomProps> = (props) => {
    const { conversations, selectedId, isLoadingMessages } = useAppSelector((state: RootState) => state.conversations);
    const { archiveSourceAvailability, activeUserInfo } = useAppSelector((state: RootState) => state.app);

    const messages = conversations[selectedId].messages;

    const classes = useClasses();
    const dispatch = useAppDispatch();
    const scrollViewTargetRef = React.useRef<HTMLDivElement>(null);
    const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);

    const [isDraggingOver, setIsDraggingOver] = React.useState(false);
    const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };
    const onDragLeave = (e: React.DragEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    const chat = useChat();

    const appConfig = useAppConfig();

    React.useEffect(() => {
        if (!shouldAutoScroll) return;
        scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
    }, [messages, shouldAutoScroll]);

    React.useEffect(() => {
        const getMessages = async () => {
            dispatch(setIsLoadingMessages(true));
            const chatMessages: IChatMessage[] = await chat.getChatMessages(selectedId);
            messages.push(...chatMessages);
            dispatch(setIsLoadingMessages(false));
        };

        if (conversations[selectedId].messages.length === 0) {
            getMessages().catch((e) => {
                log('error getting messages', e);
                dispatch(setIsLoadingMessages(false));
            });
        }

        setShouldAutoScroll(true);

        const getAvailableArchiveSources = async () => {
            const response: IAppConfigResult<IArchiveSourceAvailability> = await appConfig.getArchiveConfig();
            if (response.Success) {
                dispatch(
                    setArchiveSourceAvailability({
                        loaded: true,
                        Sources: response.Data?.Sources ?? ([] as IArchiveSources[]),
                    }),
                );
            }
        };

        if (conversations[selectedId].chatScope == ChatScope.Archive && !archiveSourceAvailability?.loaded) {
            getAvailableArchiveSources().catch((error) => {
                log('error getting archive sources', error);
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    React.useEffect(() => {
        const onScroll = () => {
            if (!scrollViewTargetRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = scrollViewTargetRef.current;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
            setShouldAutoScroll(isAtBottom);
        };

        if (!scrollViewTargetRef.current) return;

        const currentScrollViewTarget = scrollViewTargetRef.current;

        currentScrollViewTarget.addEventListener('scroll', onScroll);
        return () => {
            currentScrollViewTarget.removeEventListener('scroll', onScroll);
        };
    }, []);

    const handleSubmit = async (options: GetResponseOptions) => {
        setShouldAutoScroll(true);

        const chatInput: IChatMessage = {
            chatId: selectedId,
            timestamp: new Date().getTime(),
            userId: activeUserInfo?.id as string,
            userName: activeUserInfo?.username as string,
            content: options.value,
            type: options.messageType,
            authorRole: AuthorRoles.User,
        };

        dispatch(addMessageToConversationFromUser({ message: chatInput, chatId: selectedId }));

        const askResult = await chat.getResponse(options);

        if (null === askResult) {
            dispatch(updateAppErrorState(true));
        }

        return askResult;
    };

    return (
        <>
            {props.hiddenChat ? (
                <div className={classes.root}>
                    <div ref={scrollViewTargetRef} className={classes.scroll}>
                        <div className={classes.history}>
                            <ChatHistory
                                messages={messages}
                                onGetResponse={handleSubmit}
                                loadingMessages={isLoadingMessages}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className={classes.root}
                    onDragEnter={onDragEnter}
                    onDragOver={onDragEnter}
                    onDragLeave={onDragLeave}
                >
                    <div ref={scrollViewTargetRef} className={classes.scroll}>
                        <div className={mergeClasses(classes.history, classes.guidance)}>
                            <ChatGuidance onSubmit={handleSubmit} />
                        </div>
                        <div className={classes.history}>
                            <ChatHistory
                                messages={messages}
                                onGetResponse={handleSubmit}
                                loadingMessages={isLoadingMessages}
                            />
                        </div>
                    </div>
                    <div className={classes.input}>
                        <ChatInput isDraggingOver={isDraggingOver} onDragLeave={onDragLeave} onSubmit={handleSubmit} />
                    </div>
                </div>
            )}
            <div className={classes.bottomContent}>
                <div className={classes.informationTextContainer}>
                    <Caption1>
                        Thomas is powered by Azure OpenAI and mistakes are possible. Please check important information.
                    </Caption1>
                </div>
                <EffectivePromptsDialog />
            </div>
        </>
    );
};
